import Phaser from 'phaser';
import { MobileControls } from '../ui/MobileControls';

/**
 * HouseScene - A clean, top-down exploration level
 * 
 * Architecture:
 * - All measurements in TILES (not pixels)
 * - Single TILE_SIZE constant controls everything
 * - Rooms defined as data, not hardcoded positions
 * - Clear separation: tilemap → furniture → player → UI
 */

// ============ CONSTANTS ============
const TILE_SIZE = 20; // Base unit - everything scales from this
const MAP_COLS = 24;
const MAP_ROWS = 16;
const PLAYER_SPEED = 120;

// ============ ROOM DATA ============
interface Room {
  id: number;
  name: string;
  color: number;
}

const ROOMS: Record<number, Room> = {
  0: { id: 0, name: '', color: 0x1a1a2e }, // Outside/void
  1: { id: 1, name: 'Hallway', color: 0xdeb887 },
  2: { id: 2, name: "Kids' Bedroom", color: 0xa29bfe },
  3: { id: 3, name: 'Bathroom', color: 0x81ecec },
  4: { id: 4, name: 'Kitchen', color: 0xffeaa7 },
  5: { id: 5, name: 'Living Room', color: 0x55efc4 },
};

// Simple house layout (each number = room ID)
// prettier-ignore
const HOUSE_MAP: number[][] = [
  [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
  [0,0,0,2,2,2,2,2,2,0,3,3,3,3,0,4,4,4,4,4,4,4,0,0],
  [0,0,0,2,2,2,2,2,2,0,3,3,3,3,0,4,4,4,4,4,4,4,0,0],
  [0,0,0,2,2,2,2,2,2,0,3,3,3,3,0,4,4,4,4,4,4,4,0,0],
  [0,0,0,2,2,2,2,2,2,1,1,1,1,1,1,4,4,4,4,4,4,4,0,0],
  [0,0,0,2,2,2,2,2,2,1,1,1,1,1,1,4,4,4,4,4,4,4,0,0],
  [0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,0,0,0,0,0,0],
  [0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,0,0,0,0,0,0],
  [0,0,0,5,5,5,5,5,5,1,1,1,1,1,1,5,5,5,5,5,5,0,0,0],
  [0,0,0,5,5,5,5,5,5,1,1,1,1,1,1,5,5,5,5,5,5,0,0,0],
  [0,0,0,5,5,5,5,5,5,1,1,1,1,1,1,5,5,5,5,5,5,0,0,0],
  [0,0,0,5,5,5,5,5,5,5,5,5,5,5,5,5,5,5,5,5,5,0,0,0],
  [0,0,0,5,5,5,5,5,5,5,5,5,5,5,5,5,5,5,5,5,5,0,0,0],
  [0,0,0,5,5,5,5,5,5,5,5,5,5,5,5,5,5,5,5,5,5,0,0,0],
  [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
  [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
];

// ============ ROOM INTERACTIONS ============
interface RoomInteraction {
  id: string;
  name: string;
  description: string;
  roomId: number;
  tileX: number;
  tileY: number;
  icon: string;
  scene: string | null; // Scene to start, or null if coming soon
  available: boolean;
}

const ROOM_INTERACTIONS: RoomInteraction[] = [
  {
    id: 'wake_emily',
    name: 'Wake Emily',
    description: 'Boss Battle: The Blanket Fortress!',
    roomId: 2, // Kids' Bedroom
    tileX: 6,
    tileY: 3,
    icon: '🛏️',
    scene: 'BlanketBossScene',
    available: true,
  },
  {
    id: 'bath_time',
    name: 'Bath Time',
    description: 'Bath Time Challenge (Coming Soon)',
    roomId: 3, // Bathroom
    tileX: 12,
    tileY: 2,
    icon: '🛁',
    scene: null,
    available: false,
  },
  {
    id: 'make_breakfast',
    name: 'Make Breakfast',
    description: 'Cooking Challenge (Coming Soon)',
    roomId: 4, // Kitchen
    tileX: 18,
    tileY: 3,
    icon: '🍳',
    scene: null,
    available: false,
  },
  {
    id: 'family_time',
    name: 'Family Time',
    description: 'Story Time (Coming Soon)',
    roomId: 5, // Living Room
    tileX: 12,
    tileY: 11,
    icon: '📺',
    scene: null,
    available: false,
  },
];

// ============ SCENE ============
export class HouseScene extends Phaser.Scene {
  private player!: Phaser.GameObjects.Rectangle;
  private playerBody!: Phaser.Physics.Arcade.Body;
  private walls!: Phaser.Physics.Arcade.StaticGroup;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private mobileControls?: MobileControls;
  
  private currentRoom: number = -1;
  private roomLabel!: Phaser.GameObjects.Text;
  private interactionMarkers: Map<string, Phaser.GameObjects.Container> = new Map();
  private activeInteraction: RoomInteraction | null = null;
  private interactPrompt!: Phaser.GameObjects.Text;

  constructor() {
    super('HouseScene');
  }

  create() {
    const mapWidth = MAP_COLS * TILE_SIZE;
    const mapHeight = MAP_ROWS * TILE_SIZE;

    // 1. Draw the floor tiles
    this.drawFloor();

    // 2. Create walls (collision with void/outside)
    this.createWalls();

    // 3. Draw room walls/borders
    this.drawRoomBorders();

    // 4. Place interaction markers
    this.createInteractionMarkers();

    // 5. Create player (simple colored rectangle)
    this.createPlayer();

    // 6. Setup camera
    this.cameras.main.setBounds(0, 0, mapWidth, mapHeight);
    this.cameras.main.startFollow(this.player, true, 0.1, 0.1);
    this.cameras.main.setZoom(1.8); // Zoom in a bit to see details

    // 7. Setup physics bounds
    this.physics.world.setBounds(0, 0, mapWidth, mapHeight);

    // 8. Setup controls
    this.setupControls();

    // 9. Create UI
    this.createUI();

    // 10. Show mission intro
    this.showMissionBriefing();

    // 11. Mobile controls
    if (MobileControls.shouldShow()) {
      this.mobileControls = new MobileControls(this);
      
      // Listen for mobile interact button
      this.events.on('mobile-interact-down', () => this.interact());
    }
  }

  // ============ SETUP METHODS ============

  private drawFloor() {
    for (let row = 0; row < MAP_ROWS; row++) {
      for (let col = 0; col < MAP_COLS; col++) {
        const roomId = HOUSE_MAP[row][col];
        if (roomId === 0) continue; // Skip void

        const room = ROOMS[roomId];
        const x = col * TILE_SIZE + TILE_SIZE / 2;
        const y = row * TILE_SIZE + TILE_SIZE / 2;

        // Floor tile
        this.add.rectangle(x, y, TILE_SIZE - 1, TILE_SIZE - 1, room.color, 0.8)
          .setDepth(0);
      }
    }
  }

  private createWalls() {
    this.walls = this.physics.add.staticGroup();

    for (let row = 0; row < MAP_ROWS; row++) {
      for (let col = 0; col < MAP_COLS; col++) {
        const roomId = HOUSE_MAP[row][col];
        if (roomId !== 0) continue; // Only void tiles are walls

        const x = col * TILE_SIZE + TILE_SIZE / 2;
        const y = row * TILE_SIZE + TILE_SIZE / 2;

        const wall = this.walls.create(x, y, undefined) as Phaser.Physics.Arcade.Sprite;
        wall.setVisible(false);
        wall.setSize(TILE_SIZE, TILE_SIZE);
        wall.setImmovable(true);
        wall.refreshBody();
      }
    }
  }

  private drawRoomBorders() {
    const graphics = this.add.graphics();
    graphics.lineStyle(2, 0x2d3436, 1);
    graphics.setDepth(10);

    for (let row = 0; row < MAP_ROWS; row++) {
      for (let col = 0; col < MAP_COLS; col++) {
        const roomId = HOUSE_MAP[row][col];
        if (roomId === 0) continue;

        const x = col * TILE_SIZE;
        const y = row * TILE_SIZE;

        // Draw border if adjacent to different room
        if (row === 0 || HOUSE_MAP[row - 1][col] !== roomId) {
          graphics.lineBetween(x, y, x + TILE_SIZE, y);
        }
        if (row === MAP_ROWS - 1 || HOUSE_MAP[row + 1][col] !== roomId) {
          graphics.lineBetween(x, y + TILE_SIZE, x + TILE_SIZE, y + TILE_SIZE);
        }
        if (col === 0 || HOUSE_MAP[row][col - 1] !== roomId) {
          graphics.lineBetween(x, y, x, y + TILE_SIZE);
        }
        if (col === MAP_COLS - 1 || HOUSE_MAP[row][col + 1] !== roomId) {
          graphics.lineBetween(x + TILE_SIZE, y, x + TILE_SIZE, y + TILE_SIZE);
        }
      }
    }
  }

  private createInteractionMarkers() {
    ROOM_INTERACTIONS.forEach(interaction => {
      const x = interaction.tileX * TILE_SIZE + TILE_SIZE / 2;
      const y = interaction.tileY * TILE_SIZE + TILE_SIZE / 2;

      const container = this.add.container(x, y);
      container.setDepth(50);

      // Glow circle - gold for available, gray for coming soon
      const glowColor = interaction.available ? 0xffd700 : 0x666666;
      const glow = this.add.circle(0, 0, TILE_SIZE, glowColor, 0.3);
      container.add(glow);

      // Icon
      const icon = this.add.text(0, -2, interaction.icon, { fontSize: '16px' });
      icon.setOrigin(0.5, 0.5);
      container.add(icon);

      // Label below icon
      const label = this.add.text(0, TILE_SIZE + 2, interaction.name, {
        fontFamily: 'Georgia, serif',
        fontSize: '8px',
        color: interaction.available ? '#ffd700' : '#666666',
      });
      label.setOrigin(0.5, 0);
      container.add(label);

      // Pulse animation (stronger for available)
      if (interaction.available) {
        this.tweens.add({
          targets: glow,
          scaleX: 1.4,
          scaleY: 1.4,
          alpha: 0.1,
          duration: 800,
          yoyo: true,
          repeat: -1,
        });
        
        // Bounce for available
        this.tweens.add({
          targets: icon,
          y: -6,
          duration: 500,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }

      this.interactionMarkers.set(interaction.id, container);
    });
  }

  private createPlayer() {
    // Start in hallway (center of map)
    const startX = 11 * TILE_SIZE + TILE_SIZE / 2;
    const startY = 7 * TILE_SIZE + TILE_SIZE / 2;

    // Simple colored rectangle as player
    this.player = this.add.rectangle(startX, startY, TILE_SIZE * 0.7, TILE_SIZE * 0.9, 0x1a3a5c);
    this.player.setDepth(100);

    // Add head
    const head = this.add.circle(startX, startY - TILE_SIZE * 0.3, TILE_SIZE * 0.25, 0xffd9b3);
    head.setDepth(101);

    // Make player a physics object
    this.physics.add.existing(this.player);
    this.playerBody = this.player.body as Phaser.Physics.Arcade.Body;
    this.playerBody.setCollideWorldBounds(true);

    // Collide with walls
    this.physics.add.collider(this.player, this.walls);

    // Make head follow player
    this.events.on('update', () => {
      head.setPosition(this.player.x, this.player.y - TILE_SIZE * 0.3);
    });
  }

  private setupControls() {
    if (!this.input.keyboard) return;

    this.cursors = this.input.keyboard.createCursorKeys();
    this.input.keyboard.addKey('W');
    this.input.keyboard.addKey('A');
    this.input.keyboard.addKey('S');
    this.input.keyboard.addKey('D');

    // E to interact
    this.input.keyboard.addKey('E').on('down', () => this.interact());
    this.input.keyboard.addKey('SPACE').on('down', () => this.interact());
  }

  private createUI() {
    const { width, height } = this.cameras.main;

    // Room label (top left)
    this.roomLabel = this.add.text(8, 8, '', {
      fontFamily: 'Georgia, serif',
      fontSize: '12px',
      color: '#ffffff',
      backgroundColor: '#000000aa',
      padding: { x: 6, y: 4 },
    });
    this.roomLabel.setScrollFactor(0);
    this.roomLabel.setDepth(200);

    // Interact prompt (hidden initially)
    this.interactPrompt = this.add.text(width / 2, height - 60, '', {
      fontFamily: 'Georgia, serif',
      fontSize: '14px',
      color: '#ffd700',
      backgroundColor: '#000000dd',
      padding: { x: 12, y: 8 },
    });
    this.interactPrompt.setOrigin(0.5, 0.5);
    this.interactPrompt.setScrollFactor(0);
    this.interactPrompt.setDepth(200);
    this.interactPrompt.setVisible(false);

    // Controls hint (bottom)
    const controls = this.add.text(width / 2, height - 20, 
      '🎮 WASD/Arrows = Move  |  E/Space = Interact', {
      fontFamily: 'Georgia, serif',
      fontSize: '10px',
      color: '#888888',
    });
    controls.setOrigin(0.5, 0.5);
    controls.setScrollFactor(0);
    controls.setDepth(200);
  }

  private showMissionBriefing() {
    const { width, height } = this.cameras.main;

    // Overlay
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.9);
    overlay.setScrollFactor(0);
    overlay.setDepth(300);

    // Title
    const title = this.add.text(width / 2, height * 0.3, '🌅 MORNING ROUTINE', {
      fontFamily: 'Georgia, serif',
      fontSize: '20px',
      color: '#ffd700',
    });
    title.setOrigin(0.5, 0.5);
    title.setScrollFactor(0);
    title.setDepth(301);

    // Mission
    const mission = this.add.text(width / 2, height * 0.45,
      '🎯 MISSION: Wake Emily!\n\n' +
      'She\'s hiding in her blanket in the Kids\' Bedroom.\n' +
      'Find the 🛏️ marker and press E to interact.',
      {
        fontFamily: 'Georgia, serif',
        fontSize: '12px',
        color: '#ffffff',
        align: 'center',
        lineSpacing: 6,
      });
    mission.setOrigin(0.5, 0.5);
    mission.setScrollFactor(0);
    mission.setDepth(301);

    // Prompt
    const prompt = this.add.text(width / 2, height * 0.7, '— Press any key to start —', {
      fontFamily: 'Georgia, serif',
      fontSize: '14px',
      color: '#22c55e',
    });
    prompt.setOrigin(0.5, 0.5);
    prompt.setScrollFactor(0);
    prompt.setDepth(301);

    // Blink
    this.tweens.add({
      targets: prompt,
      alpha: 0.3,
      duration: 500,
      yoyo: true,
      repeat: -1,
    });

    // Dismiss
    if (this.input.keyboard) {
      this.input.keyboard.once('keydown', () => {
        this.tweens.add({
          targets: [overlay, title, mission, prompt],
          alpha: 0,
          duration: 300,
          onComplete: () => {
            overlay.destroy();
            title.destroy();
            mission.destroy();
            prompt.destroy();
          },
        });
      });
    }
  }

  // ============ GAME LOGIC ============

  private getRoomAt(x: number, y: number): number {
    const col = Math.floor(x / TILE_SIZE);
    const row = Math.floor(y / TILE_SIZE);
    if (row >= 0 && row < MAP_ROWS && col >= 0 && col < MAP_COLS) {
      return HOUSE_MAP[row][col];
    }
    return 0;
  }

  private getNearbyInteraction(): RoomInteraction | null {
    const playerTileX = Math.floor(this.player.x / TILE_SIZE);
    const playerTileY = Math.floor(this.player.y / TILE_SIZE);

    for (const interaction of ROOM_INTERACTIONS) {
      const dist = Math.abs(playerTileX - interaction.tileX) + Math.abs(playerTileY - interaction.tileY);
      if (dist <= 1) {
        return interaction;
      }
    }
    return null;
  }

  private interact() {
    if (!this.activeInteraction) return;

    if (this.activeInteraction.available && this.activeInteraction.scene) {
      // Launch the TransitionScene for perspective transformation
      this.scene.start('TransitionScene', {
        fromScene: 'HouseScene',
        toScene: this.activeInteraction.scene,
        roomData: {
          x: this.activeInteraction.tileX * TILE_SIZE,
          y: this.activeInteraction.tileY * TILE_SIZE,
          roomId: this.activeInteraction.roomId,
        },
      });
    } else {
      // Show "coming soon" message
      this.showComingSoon(this.activeInteraction.name);
    }
  }

  private showComingSoon(name: string) {
    const { width, height } = this.cameras.main;
    const msg = this.add.text(width / 2, height / 2, `🚧 ${name}\nComing Soon!`, {
      fontFamily: 'Georgia, serif',
      fontSize: '16px',
      color: '#ffcc00',
      backgroundColor: '#000000dd',
      padding: { x: 16, y: 12 },
      align: 'center',
    });
    msg.setOrigin(0.5, 0.5);
    msg.setScrollFactor(0);
    msg.setDepth(300);

    this.time.delayedCall(1500, () => {
      this.tweens.add({
        targets: msg,
        alpha: 0,
        duration: 300,
        onComplete: () => msg.destroy(),
      });
    });
  }

  // ============ UPDATE LOOP ============

  update() {
    if (!this.playerBody) return;

    let vx = 0;
    let vy = 0;

    // Mobile controls (joystick)
    if (this.mobileControls) {
      const movement = this.mobileControls.getMovementVector();
      if (movement.x !== 0 || movement.y !== 0) {
        vx = movement.x * PLAYER_SPEED;
        vy = movement.y * PLAYER_SPEED;
      }
      this.mobileControls.update();
    }

    // Keyboard controls (if no mobile input)
    if (vx === 0 && vy === 0 && this.cursors && this.input.keyboard) {
      const keyboard = this.input.keyboard;
      
      if (this.cursors.left?.isDown || keyboard.addKey('A').isDown) vx = -PLAYER_SPEED;
      else if (this.cursors.right?.isDown || keyboard.addKey('D').isDown) vx = PLAYER_SPEED;

      if (this.cursors.up?.isDown || keyboard.addKey('W').isDown) vy = -PLAYER_SPEED;
      else if (this.cursors.down?.isDown || keyboard.addKey('S').isDown) vy = PLAYER_SPEED;

      // Normalize diagonal
      if (vx !== 0 && vy !== 0) {
        vx *= 0.707;
        vy *= 0.707;
      }
    }

    this.playerBody.setVelocity(vx, vy);

    // Update room label
    const roomId = this.getRoomAt(this.player.x, this.player.y);
    if (roomId !== this.currentRoom) {
      this.currentRoom = roomId;
      const room = ROOMS[roomId];
      this.roomLabel.setText(room.name ? `📍 ${room.name}` : '');
    }

    // Check for nearby interactions
    const nearbyInteraction = this.getNearbyInteraction();
    if (nearbyInteraction !== this.activeInteraction) {
      this.activeInteraction = nearbyInteraction;
      if (nearbyInteraction) {
        const prefix = nearbyInteraction.available ? '🎮 Press E:' : '🔒';
        this.interactPrompt.setText(`${prefix} ${nearbyInteraction.description}`);
        this.interactPrompt.setVisible(true);
      } else {
        this.interactPrompt.setVisible(false);
      }
    }
  }
}
